import React, { Suspense } from "react";
import { lazyWithRetry } from "./utils/lazyWithRetry";
import { Route, Routes } from "react-router-dom";
import Layout from "./components/Layout";
import LoadingPage from "./components/LoadingPage";
import ErrorBoundary from "./components/ErrorBoundary";
import { useAuth } from "./hooks/useAuth";
import { ContextUser } from "./types/Users";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNativeShell } from "./native/useNativeShell";
import { useReportTimezone } from "./hooks/useReportTimezone";
import { usePageViews } from "./analytics/usePageViews";
// custom styles removed (legacy SCSS)
const Home = lazyWithRetry(() => import("./pages/Home"));
const Login = lazyWithRetry(() => import("./pages/Login"));
const Welcome = lazyWithRetry(() => import("./pages/Welcome"));
const Register = lazyWithRetry(() => import("./pages/Register"));
const OurSexplorationList = lazyWithRetry(
  () => import("pages/OurSexplorationList")
);
const Sessions = lazyWithRetry(
  () => import("pages/OurSexplorationList/Sessions")
);
const Directions = lazyWithRetry(
  () => import("pages/OurSexplorationList/Directions")
);
const ManageQuestions = lazyWithRetry(
  () => import("pages/OurSexplorationList/ManageQuestions")
);
const AnswerQuestions = lazyWithRetry(
  () => import("pages/OurSexplorationList/AnswerQuestions")
);
const RandomQuestions = lazyWithRetry(
  () => import("pages/OurSexplorationList/RandomQuestion")
);
const AnswerSetOfQuestions = lazyWithRetry(
  () => import("pages/OurSexplorationList/AnswerSetOfQuestions")
);
const QuestionsInOrder = lazyWithRetry(
  () => import("pages/OurSexplorationList/QuestionsInOrder")
);
const QuestionsSpouseAnswered = lazyWithRetry(
  () => import("pages/OurSexplorationList/QuestionSpouseAnswered")
);
const SpontaneousResponsiveQuiz = lazyWithRetry(
  () => import("pages/SpontaneousResponsiveQuiz")
);
const FindMatches = lazyWithRetry(
  () => import("pages/OurSexplorationList/FindMatches")
);
const ExplorationGuide = lazyWithRetry(
  () => import("pages/OurSexplorationList/ExplorationGuide")
);
const ExportList = lazyWithRetry(
  () => import("pages/OurSexplorationList/ExportList")
);
const SpecificQuestion = lazyWithRetry(
  () => import("pages/OurSexplorationList/SpecificQuestion")
);
const QuestionsAlreadyAnswered = lazyWithRetry(
  () => import("pages/OurSexplorationList/QuestionsAlreadyAnswered")
);
const ViewAnswers = lazyWithRetry(
  () => import("pages/OurSexplorationList/ViewAnswers")
);
const IntimacyInsights = lazyWithRetry(() => import("pages/IntimacyInsights"));
// Closed beta (user 555). The page itself redirects anyone else home.
const PrivateGuide = lazyWithRetry(() => import("pages/PrivateGuide"));
const Settings = lazyWithRetry(() => import("pages/Settings"));
const ApiExplorer = lazyWithRetry(() => import("pages/Settings/ApiExplorer"));
const ManageActivities = lazyWithRetry(
  () => import("pages/Settings/ManageActivities")
);
const WhatsNew = lazyWithRetry(() => import("pages/Settings/WhatsNew"));
const AdminDashboard = lazyWithRetry(() => import("pages/Admin"));
const Logout = lazyWithRetry(() => import("pages/Logout"));
const QuestionsBySection = lazyWithRetry(
  () => import("pages/OurSexplorationList/QuestionsBySection")
);
const Invite = lazyWithRetry(() => import("pages/Invite"));
const SignIn = lazyWithRetry(() => import("pages/SignIn"));
const GenerateInvite = lazyWithRetry(
  () => import("pages/OurSexplorationList/GenerateInvite")
);
const IntimacyAdventCalendar = lazyWithRetry(
  () => import("pages/IntimacyAdventCalendar")
);
const BrowseAdventCards = lazyWithRetry(
  () => import("pages/IntimacyAdventCalendar/Cards")
);
const CreateCustomCard = lazyWithRetry(
  () => import("pages/IntimacyAdventCalendar/CreateCard")
);
const ScheduleAdventCalendar = lazyWithRetry(
  () => import("pages/IntimacyAdventCalendar/Schedule")
);
const ViewAdventSchedule = lazyWithRetry(
  () => import("pages/IntimacyAdventCalendar/ViewSchedule")
);
const GeneratePDF = lazyWithRetry(
  () => import("./pages/IntimacyAdventCalendar/GeneratePDF")
);
const SharedAdventCard = lazyWithRetry(
  () => import("./pages/IntimacyAdventCalendar/SharedCard")
);
// The webinar pages live on live.uncoveringintimacy.com (LiveApp.tsx). The old
// /live addresses redirect there, token intact; see pages/Live/Redirect.tsx.
const LiveRedirect = lazyWithRetry(() => import("./pages/Live/Redirect"));
const IntimacyStats = lazyWithRetry(
  () => import("./pages/IntimacyInsights/Stats")
);
const Activities = lazyWithRetry(() => import("./pages/Activities"));
const AdminTest = lazyWithRetry(() => import("./pages/AdminTest"));
const Analytics = lazyWithRetry(() => import("./pages/Analytics"));
const ConversationStarters = lazyWithRetry(
  () => import("./pages/ConversationStarters")
);
const ManageConversationStarters = lazyWithRetry(
  () => import("./pages/Admin/ManageConversationStarters")
);
const CoursePage = lazyWithRetry(() => import("./pages/Courses"));
const CourseUnitPage = lazyWithRetry(() => import("./pages/Courses/Unit"));
const CourseAnswersPage = lazyWithRetry(
  () => import("./pages/Courses/Answers")
);
const CourseWelcome = lazyWithRetry(() => import("./pages/Courses/Welcome"));
const CourseRoster = lazyWithRetry(() => import("./pages/Admin/CourseRoster"));
const CourseQa = lazyWithRetry(() => import("./pages/Admin/CourseQa"));
const WebinarHost = lazyWithRetry(() => import("./pages/Admin/WebinarHost"));

// Null-object sentinel for logged-out/guest reads. ID 0 is the "not a real
// user" marker (see useSpontaneousResponsiveDesireResults, which gates on
// `user.ID !== 0`), so consumers can read fields without null checks.
export const GUEST_USER: ContextUser = {
  ID: 0,
  oursexplorationlist_current_session_id: 0,
  token: "",
  oursexplorationlist_simplified_version: 0,
};

export const UserContext = React.createContext<ContextUser>(GUEST_USER);

function App() {
  const { isLoggedIn, user } = useAuth();

  // Android hardware back button + external links out to the system browser.
  // No-ops on the web.
  useNativeShell();

  // Page-level usage stats: one beacon per screen with its route template
  // only (no user, no address, no content). See src/analytics/.
  usePageViews();

  // Keep the member's timezone current, so scheduled messages (course unlock
  // nudges today, anything later) land in their morning rather than at one
  // fixed UTC hour. Sends only when it has actually changed.
  useReportTimezone(isLoggedIn, user?.timezone);

  // The app-row half of `user` is merged in AFTER login, so these two can be
  // undefined for a real user mid-hydration. Consumers rely on them existing
  // (that is what GUEST_USER guarantees), so default them here, once, to the
  // same 0 sentinel rather than making every consumer coalesce.
  const contextUser: ContextUser = user
    ? {
        ...user,
        oursexplorationlist_current_session_id:
          user.oursexplorationlist_current_session_id ?? 0,
        oursexplorationlist_simplified_version:
          user.oursexplorationlist_simplified_version ?? 0,
      }
    : GUEST_USER;

  return (
    <UserContext.Provider value={contextUser}>
      <Layout>
        <ErrorBoundary>
          <Suspense fallback={<LoadingPage />}>
            {isLoggedIn ? (
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/Logout" element={<Logout />} />
                <Route path="/Settings" element={<Settings />} />
                <Route
                  path="/Settings/ManageActivities"
                  element={<ManageActivities />}
                />
                <Route path="/Settings/WhatsNew" element={<WhatsNew />} />
                <Route path="/Settings/ApiExplorer" element={<ApiExplorer />} />
                <Route path="/Admin" element={<AdminDashboard />} />
                <Route
                  path="/Admin/ManageConversationStarters"
                  element={<ManageConversationStarters />}
                />
                <Route
                  path="/OurSexplorationList"
                  element={<OurSexplorationList />}
                />
                <Route
                  path="/OurSexplorationList/Sessions"
                  element={<Sessions />}
                />
                <Route
                  path="/OurSexplorationList/Directions"
                  element={<Directions />}
                />
                <Route
                  path="/OurSexplorationList/ManageQuestions"
                  element={<ManageQuestions />}
                />
                <Route
                  path="/OurSexplorationList/AnswerQuestions/:spouse"
                  element={<AnswerQuestions />}
                />
                <Route
                  path="/OurSexplorationList/AnswerQuestions/Random/:spouse"
                  element={<RandomQuestions />}
                />
                <Route
                  path="/OurSexplorationList/AnswerQuestions/Set/:count/:spouse"
                  element={<AnswerSetOfQuestions />}
                />
                <Route
                  path="/OurSexplorationList/AnswerQuestions/InOrder/:spouse"
                  element={<QuestionsInOrder />}
                />
                <Route
                  path="/OurSexplorationList/AnswerQuestions/BySection/:section/:spouse"
                  element={<QuestionsBySection />}
                />
                <Route
                  path="/OurSexplorationList/AnswerQuestions/SpouseAnswered/:spouse"
                  element={<QuestionsSpouseAnswered />}
                />
                <Route
                  path="/OurSexplorationList/AnswerQuestions/Specific/:spouse/:questionId/:returnTo"
                  element={<SpecificQuestion />}
                />
                <Route
                  path="/OurSexplorationList/AnswerQuestions/AlreadyAnswered/:spouse"
                  element={<QuestionsAlreadyAnswered />}
                />
                <Route
                  path="/OurSexplorationList/ViewAnswers/:spouse"
                  element={<ViewAnswers />}
                />
                <Route
                  path="/OurSexplorationList/FindMatches"
                  element={<FindMatches />}
                />
                <Route
                  path="/OurSexplorationList/ExplorationGuide"
                  element={<ExplorationGuide />}
                />
                <Route
                  path="/OurSexplorationList/Export"
                  element={<ExportList />}
                />
                <Route
                  path="/OurSexplorationList/InviteSpouse"
                  element={<GenerateInvite />}
                />
                <Route
                  path="/SpontaneousResponsiveQuiz"
                  element={<SpontaneousResponsiveQuiz />}
                />
                <Route
                  path="/IntimacyInsights"
                  element={<IntimacyInsights />}
                />
                <Route
                  path="/IntimacyInsights/Stats"
                  element={<IntimacyStats />}
                />
                <Route path="/PrivateGuide" element={<PrivateGuide />} />
                <Route
                  path="/IntimacyAdventCalendar"
                  element={<IntimacyAdventCalendar />}
                />
                <Route
                  path="/IntimacyAdventCalendar/Cards"
                  element={<BrowseAdventCards />}
                />
                <Route
                  path="/IntimacyAdventCalendar/CreateCard"
                  element={<CreateCustomCard />}
                />
                <Route
                  path="/IntimacyAdventCalendar/Schedule"
                  element={<ScheduleAdventCalendar />}
                />
                <Route
                  path="/IntimacyAdventCalendar/ViewSchedule"
                  element={<ViewAdventSchedule />}
                />
                <Route
                  path="/IntimacyAdventCalendar/GeneratePDF"
                  element={<GeneratePDF />}
                />
                {/* A shared advent card. The recipient is usually logged out,
                    but may be signed in on a shared device, so the same page
                    is registered in both route trees. */}
                <Route path="/advent/:token" element={<SharedAdventCard />} />
                {/* Old webinar links: redirect to the live host. Public
                    (registrants are guests), so registered in both trees. */}
                <Route path="/live" element={<LiveRedirect page="home" />} />
                <Route
                  path="/live/:slug"
                  element={<LiveRedirect page="landing" />}
                />
                <Route
                  path="/live/:slug/watch"
                  element={<LiveRedirect page="watch" />}
                />
                <Route path="/Activities" element={<Activities />} />
                <Route
                  path="/ConversationStarters"
                  element={<ConversationStarters />}
                />
                <Route path="/AdminTest" element={<AdminTest />} />
                <Route path="/Analytics" element={<Analytics />} />
                {/* An emailed sign-in link opened on a device that is
                    already signed in (a shared phone): it signs in the
                    link's account instead, as a password login would. */}
                <Route path="/SignIn" element={<SignIn />} />
                <Route path="/Courses/:slug" element={<CoursePage />} />
                {/* Everything she has written in the course, to read and
                    print. A static segment, so it wins over :unitKey. */}
                <Route
                  path="/Courses/:slug/answers"
                  element={<CourseAnswersPage />}
                />
                <Route
                  path="/Courses/:slug/:unitKey"
                  element={<CourseUnitPage />}
                />
                {/* The worksheet step of a "worksheet first" unit has its own
                    address, so submitting turns a page and the back button
                    behaves. Same component, different step. */}
                <Route
                  path="/Courses/:slug/:unitKey/worksheet"
                  element={<CourseUnitPage />}
                />
                <Route path="/Admin/Courses/:slug" element={<CourseRoster />} />
                <Route path="/Admin/CourseQA" element={<CourseQa />} />
                <Route path="/Admin/CourseQA/:slug" element={<CourseQa />} />
                <Route path="/Admin/Webinar" element={<WebinarHost />} />
                <Route path="/Admin/Webinar/:slug" element={<WebinarHost />} />
              </Routes>
            ) : (
              <Routes>
                <Route path="/" element={<Welcome />} />
                <Route path="/Login" element={<Login />} />
                <Route path="/Register" element={<Register />} />
                <Route
                  path="/SpontaneousResponsiveQuiz"
                  element={<SpontaneousResponsiveQuiz />}
                />
                <Route path="/Invite" element={<Invite />} />
                {/* Passwordless sign-in link (spouse invite, or "email me a
                    sign-in link"). */}
                <Route path="/SignIn" element={<SignIn />} />
                {/* The gift link out of the advent notice email. Public: no
                    session, no auth header, and registered here as well as in
                    the logged-in tree. */}
                <Route path="/advent/:token" element={<SharedAdventCard />} />
                {/* Old webinar links: redirect to the live host. */}
                <Route path="/live" element={<LiveRedirect page="home" />} />
                <Route
                  path="/live/:slug"
                  element={<LiveRedirect page="landing" />}
                />
                <Route
                  path="/live/:slug/watch"
                  element={<LiveRedirect page="watch" />}
                />
                <Route path="/Settings/WhatsNew" element={<WhatsNew />} />
                {/* A shared course link opened without a session: name the
                    course and point at the store, rather than dropping the
                    visitor on the generic welcome screen. */}
                <Route path="/Courses/:slug" element={<CourseWelcome />} />
                <Route
                  path="/Courses/:slug/:unitKey"
                  element={<CourseWelcome />}
                />

                <Route path="*" element={<Welcome />} />
              </Routes>
            )}
          </Suspense>
        </ErrorBoundary>
        <div className="mb-5"></div>

        {/* Toasts default to the top of the screen, which is under the status
            bar once the app draws edge to edge. Nudge them below it; the inset
            is 0 on the web, leaving the default 1rem offset intact. */}
        <ToastContainer
          autoClose={3000}
          style={{ top: "calc(1rem + env(safe-area-inset-top))" }}
        />
      </Layout>
    </UserContext.Provider>
  );
}

export default App;
